import { Country, SubscriptionPlan } from '~/app/auth/enums/TenantSubscriptionPlan';
import { LOCALE } from '~/app/enums/Locale';
import { Brand } from '~/app/types/branded';
import { createMarketingMetricsDriver, MarketingMetricsDriverName } from '~/services/marketing/metrics/MarketingMetricsDriver';
import { createMarketingMetricsHash } from '~/services/marketing/metrics/MarketingMetricsHash';

export const MARKETING_METRICS_EVENT_NAME = {
    SIGNUP_START: 'signup_start',
    SIGNUP_STEP: 'signup_step',
    SIGNUP_COMPLETED: 'signup_completed',
    SIGNUP_FAILED: 'signup_failed',
} as const;

export const MARKETING_METRICS_ERROR_TYPE = {
    WARNING: 'warning',
    ERROR: 'error',
} as const;

export type MarketingMetricsEventName = Enum<typeof MARKETING_METRICS_EVENT_NAME>;

type SensitiveValue = Brand<string, 'SensitiveValue'>;
export type Sensitive<T> = T extends object ? { [K in keyof T]: Sensitive<T[K]> } : SensitiveValue;

type UserData = Sensitive<{
    email_address: string;
    phone_number: string;
    first_name: string;
    last_name: string;
}>;

// Funnel telemetry carried by every inbound-flow signup event (Phase 5 spec §4.5): the funnel name
// keeps GA4 funnels comparable across step add/remove/reorder, and `variant` is the A/B arm.
type SignupFunnelMeta = {
    plan: SubscriptionPlan | null;
    funnel_name?: string;
    variant?: string | null;
};

export type MarketingMetricsEvents = {
    [MARKETING_METRICS_EVENT_NAME.SIGNUP_START]: SignupFunnelMeta;
    // step_name + step_order survive flow changes; the step's collected answer(s) are appended as extra
    // keys (sensitive ones SHA256-hashed into `user_data`), hence the open Record.
    [MARKETING_METRICS_EVENT_NAME.SIGNUP_STEP]: SignupFunnelMeta & {
        step_name: string;
        step_order?: number;
    } & Record<string, unknown>;
    [MARKETING_METRICS_EVENT_NAME.SIGNUP_COMPLETED]: SignupFunnelMeta & {
        account_type?: string | null;
        user_data?: Partial<UserData>;
    } & Record<string, unknown>;
    [MARKETING_METRICS_EVENT_NAME.SIGNUP_FAILED]: SignupFunnelMeta & {
        error_type?: typeof MARKETING_METRICS_ERROR_TYPE.WARNING | typeof MARKETING_METRICS_ERROR_TYPE.ERROR;
        failed_at_step?: string;
        step_order?: number;
        user_data?: Partial<UserData>;
        reason?: 'individual' | 'user_exists';
    };
};

/**
 * Initialize marketing metrics and expose an interface to communicate with their dashboard.
 */
const useMarketingMetrics = () => {
    const { getCurrentLocale } = useTranslation();
    const {
        public: { marketingMetricsUrl, marketingMetricsCookieName, marketingMetricsDriver, marketingMetricsLocaleName },
    } = useRuntimeConfig();
    const { COOKIE_DURATIONS, setCookie } = useCookieManager();
    const { query, meta } = useRoute();

    const driver = createMarketingMetricsDriver(marketingMetricsDriver as MarketingMetricsDriverName);
    const hash = createMarketingMetricsHash();

    const MARKETING_METRICS_EXCLUDE_META = 'excludeFromMarketingMetrics' as const;

    const push = <TKey extends keyof MarketingMetricsEvents>(event: TKey, data: MarketingMetricsEvents[TKey]): void => {
        const locale = getCurrentLocale();
        const country = locale.substring(0, 2) as Country;

        driver.push({
            event,
            country,
            locale,
            ...data,
        });
    };

    const getDomain = () => {
        const sanitized = marketingMetricsUrl.replace('https://', '').replace('wwww.', '').split('.').slice(-2).join('.');

        return `.${sanitized}`;
    };

    const setCookieFromQuery = (): void => {
        const cookieValue = getInQuery(marketingMetricsCookieName);
        if (!cookieValue || Array.isArray(cookieValue)) {
            return;
        }

        const domain = getDomain();
        setCookie(marketingMetricsCookieName, COOKIE_DURATIONS.oneYear, cookieValue, {
            domain,
        });
    };

    const setLocaleFromQuery = (): void => {
        const localeInQuery = getInQuery(marketingMetricsLocaleName);
        const locale = Object.values(LOCALE).find((locale) => locale === localeInQuery);

        if (!locale || getCurrentLocale() === locale) {
            return;
        }

        useSetLocale(locale);
    };

    const getInQuery = (key: string) => {
        return query[key];
    };

    const isPrevented = () => !!meta[MARKETING_METRICS_EXCLUDE_META];

    const init = async () => {
        const { isReady } = useRouter();
        await isReady();

        if (isPrevented()) {
            return;
        }

        driver.init();
        setCookieFromQuery();
        setLocaleFromQuery();
    };

    init();

    return { push, hash, MARKETING_METRICS_EXCLUDE_META };
};

export default useMarketingMetrics;
